﻿import React, { useState, useEffect } from 'react';
import { NmrSolvent } from '../models/nmr-solvent';
import NmrInternalStandardsSolventsLookup from './NmrInternalStandardsSolventsLookup';
import { NmrInternalStandard } from '../models/nmr-internal-standard';
import { NmrSolventType } from '../models/nmr-solvent-type';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import { EditType, ModalMessageType } from '../models/enums';
import { CrudFunctions } from '../utilities/crud-functions';

import { Button } from 'devextreme-react/button';
import { Popup as DialogPopup } from 'devextreme-react/popup';
import Guid from 'devextreme/core/guid';

import { ReactGrid, Id, Cell, CellChange, TextCell, Column, Row, CellStyle, Range, DefaultCellTypes } from "@silevis/reactgrid";
import "@silevis/reactgrid/styles.css";

const SOLVENT_ID_COLUMN_IDX = 0;
const EDIT_TYPE_COLUMN_ID = "editType";

const initColumns: Column[] = [
    { columnId: "solventId", width: -20 },
    { columnId: EDIT_TYPE_COLUMN_ID, width: -20 },
    { columnId: "internalStandardName", width: 125, resizable: true },
    { columnId: "refWeight", width: 110, resizable: true },
    { columnId: "sampleName", width: 100 },
    { columnId: "sampleWeight", width: 110, resizable: true }
];

/* ORIG
const initColumns: Column[] = [
    { columnId: "solventId", width: -20 },
    { columnId: EDIT_TYPE_COLUMN_ID, width: -20 },
    { columnId: "sampleName", width: 100 },
    { columnId: "sampleBatchName", width: 125, resizable: true },
    { columnId: "sampleWeight", width: 110, resizable: true },
    { columnId: "solventName", width: 125, resizable: true },
    { columnId: "solventLotNumber", width: 125, resizable: true },
    { columnId: "internalStandardName", width: 125, resizable: true },
    { columnId: "internalStandardProductNumber", width: 125, resizable: true },
    { columnId: "internalStandardProductName", width: 250, resizable: true },
    { columnId: "refBatchName", width: 125, resizable: true },
    { columnId: "refWeight", width: 110, resizable: true },
    { columnId: INTERNAL_STANDARD_SOLVENT_LOOKUP_COLUMN_ID, width: 35 }
];
*/


const headerRow: Row = {
    rowId: "header",
    cells: [
        { type: "header", text: "", className: "reactgrid-hidden-column" },
        { type: "header", text: "", className: "" },
        { type: "header", text: "Internal Standard", className: "reactgrid-header-cell" },
        { type: "header", text: "Weight (mg)", className: "reactgrid-header-cell" },
        { type: "header", text: "Sample Name", className: "reactgrid-header-cell" },
        { type: "header", text: "Weight (mg)", className: "reactgrid-header-cell" }
    ]
};

/* ORIG
const headerRow: Row = {
    rowId: "header",
    cells: [
        { type: "header", text: "", className: "reactgrid-hidden-column" },
        { type: "header", text: "", className: "" },
        { type: "header", text: "Sample Name", className: "reactgrid-header-cell" },
        { type: "header", text: "Batch Name", className: "reactgrid-header-cell" },
        { type: "header", text: "Weight (mg)", className: "reactgrid-header-cell" },
        { type: "header", text: "Solvent", className: "reactgrid-header-cell" },
        // { type: "header", text: "Catalog #", className: "reactgrid-header-cell" },
        { type: "header", text: "Lot #", className: "reactgrid-header-cell" },
        // { type: "header", text: "Chemical ID", className: "reactgrid-header-cell" },
        // { type: "header", text: "", className: "reactgrid-header-cell" },
        { type: "header", text: "Internal Standard", className: "reactgrid-header-cell" },
        { type: "header", text: "Product #", className: "reactgrid-header-cell" },
        { type: "header", text: "Product Name", className: "reactgrid-header-cell" },
        { type: "header", text: "Lot #", className: "reactgrid-header-cell" },
        { type: "header", text: "Weight (mg)", className: "reactgrid-header-cell" },
        { type: "header", text: "", className: "reactgrid-header-cell" },
    ]
};
*/

let key = 1;

export interface EditIconCell extends Cell {
    type: 'editIcon';
    text: string;
}

export interface Props {
    benchSheetId?: string;
    onClose?: () => void;
}

export default function NmrSolvents(props: Props) {
    const [samples, setSamples] =
        useState<NmrSolvent[]>();

    const [selectedSample, setSelectedSample] =
        useState<NmrSolvent>();

    const [selectedSolventId, setSelectedSolventId] =
        useState<string>();

    const [rows, setRows] =
        useState<Row[]>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    const [columns, setColumns] =
        useState<Column[]>(initColumns);

    useEffect(() => {
        getAllData();
    }, []);

    useEffect(() => {
        getRows();
    }, [samples]);

    key++;

    return (
        <div>
            <div className="pw-comp-row pw-margin-top-05">
                <div className="title-bar-spacer" />
                <div className="save-toolbar pw-margin-right-05">
                    <Button className="title-bar-button"
                        text="Add Row" icon="plus" type="success"
                        hint="add row"
                        onClick={onAddRowClick} />

                    <Button id="saveButton" text="Save" icon="save" type="success"
                        className="pw-button"
                        hint="save changes"
                        height={'1.8rem'}
                        onClick={onSaveClick} />

                    <Button id="closeButton" text="Close" icon="close" type="normal"
                        className="pw-button"
                        hint="close dialog"
                        height={'1.8rem'}
                        onClick={onCloseClick} />
                </div>
            </div>

            <div className="pw-margin-top-1">
                <ReactGrid
                    enableFillHandle={true}
                    enableRangeSelection={true}
                    rows={rows ? rows : []} columns={columns}
                    onSelectionChanging={onSelectionChanging}
                    onColumnResized={handleColumnResize}
                    onCellsChanged={handleChanges} />
            </div>

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }

        </div>
    );

    function handleColumnResize(ci: Id, width: number) {
        if (!columns)
            return;

        setColumns((prevColumns) => {
            const columnIndex = prevColumns.findIndex(el => el.columnId === ci);
            const resizedColumn = prevColumns[columnIndex];
            const updatedColumn = { ...resizedColumn, width };
            prevColumns[columnIndex] = updatedColumn;
            return [...prevColumns];
        });
    }

    function onAddRowClick() {
        let editedSamples = [...samples];

        const rowCount = editedSamples.length;
        const newIndex = rowCount + 1;

        let newSample: NmrSolvent = {
            indexId: newIndex,
            internalStandardName: `I${newIndex}`,
            sampleName: `S${newIndex}`,
            solventId: new Guid().toString(),
            editType: EditType.Insert
        };

        editedSamples.push(newSample);

        setSamples(editedSamples);
    }

    function onSelectionChanging(selectedRanges: Range[]): boolean {
        console.log("onSelectionChanging");
        console.log(selectedRanges);

        if (selectedRanges && selectedRanges.length > 0 &&
            selectedRanges[0].columns && selectedRanges[0].columns.length > 0 &&
            selectedRanges[0].rows && selectedRanges[0].rows.length > 0) {

            const rowId = selectedRanges[0].rows[0].rowId;

            //console.log("rowId:");
            //console.log(rowId);

            const selectedSolventIdCell = selectedRanges[0].first.row.cells[SOLVENT_ID_COLUMN_IDX] as any;
            const solventId = selectedSolventIdCell.text;

            //console.log("solventId:");
            //console.log(solventId);

            setSelectedSolventId(solventId);

        }

        return true;
    }

    function handleChanges(changes: CellChange<TextCell>[]) {
        setSamples((prevSamples) => applyChangesToSamples(changes, prevSamples));
    };

    function applyChangesToSamples(changes: CellChange<TextCell>[], prevSamples: NmrSolvent[]): NmrSolvent[] {
        //console.log("changes:");
        //console.log(changes);

        changes.forEach((change) => {
            const sampleIndex = change.rowId;
            const fieldName = change.columnId;

            prevSamples[sampleIndex][fieldName] = change.newCell.text;

            if (prevSamples[sampleIndex].editType !== EditType.Insert)
                prevSamples[sampleIndex].editType = EditType.Update;
        });

        return [...prevSamples];
    };

    async function onSaveClick() {
        //todo - any validation

        samples.forEach((sample) => {
            sample.benchSheetId = props.benchSheetId;
        });

        await updateNmrSolvents()
            .then((data) => {
                getNmrSolvents();
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Updating NMR Solvents");
            });
    }

    function onCloseClick() {
        const anyNonSavedChanges = samples.some(a => a.editType !== EditType.None);

        //todo - ask user to close anyway


        if (props.onClose)
            props.onClose();
    }

    async function getNmrSolvents() {
        await CrudFunctions.getData(`benchsheets/nmrsolventsbybenchsheetid/${props.benchSheetId}`)
            .then((data) => {

                console.log("NMR solvents:")
                console.log(data);

                setSamples(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving NMR Solvents");
            });
    }

    async function updateNmrSolvents() {
        console.log("about to send updates to db-->");

        return await CrudFunctions.putData("benchsheets/nmrsolvents", samples)
    }

    async function getAllData() {
        await Promise.all([
            getNmrSolvents()
        ]);
    }

    function getRows() {
        //console.log("getRows");
        //console.log("samples...");
        //console.log(samples);

        let createdRows: Row[] = [];

        if (samples) {
            createdRows = [
                headerRow,
                ...samples.map<Row<DefaultCellTypes>>((solvent, idx) => ({
                    rowId: idx,
                    cells: [
                        { type: "text", text: solvent.solventId ? solvent.solventId : "", nonEditable: true, className: "reactgrid-hidden-column" },
                        { type: "text", text: "", nonEditable: true, className: getEditTypeTemplate(solvent) },
                        { type: "text", text: solvent.internalStandardName ? solvent.internalStandardName : "", className: 'reactgrid-cell pw-bold reactgrid-horizontal-center' },
                        { type: "text", text: solvent.refWeight ? solvent.refWeight.toString() : "", className: "reactgrid-cell" },
                        { type: "text", text: solvent.sampleName ? solvent.sampleName : "", className: "reactgrid-cell pw-bold reactgrid-horizontal-center" },
                        { type: "text", text: solvent.sampleWeight ? solvent.sampleWeight.toString() : "", className: 'reactgrid-cell' }
                    ]
                }))
            ];

            /*
            createdRows = [
                headerRow,
                ...samples.map<Row<DefaultCellTypes>>((solvent, idx) => ({
                    rowId: idx,
                    cells: [
                        { type: "text", text: solvent.solventId ? solvent.solventId : "", nonEditable: true, className: "reactgrid-hidden-column" },
                        { type: "text", text: "", nonEditable: true, className: getEditTypeTemplate(solvent) },
                        { type: "text", text: solvent.sampleName ? solvent.sampleName : "", className: "reactgrid-cell pw-bold reactgrid-horizontal-center" },
                        { type: "text", text: solvent.sampleBatchName ? solvent.sampleBatchName : "", className: "reactgrid-cell" },
                        { type: "text", text: solvent.sampleWeight ? solvent.sampleWeight.toString() : "", className: 'reactgrid-cell' },
                        { type: "text", text: solvent.solventName ? solvent.solventName : "", className: "reactgrid-cell" },
                        { type: "text", text: solvent.solventLotNumber ? solvent.solventLotNumber : "", className: "reactgrid-cell" },
                        { type: "text", text: solvent.internalStandardName ? solvent.internalStandardName : "", className: 'reactgrid-cell pw-bold reactgrid-horizontal-center' },
                        { type: "text", text: solvent.internalStandardProductNumber ? solvent.internalStandardProductNumber : "", className: 'reactgrid-cell' },
                        { type: "text", text: solvent.internalStandardProductName ? solvent.internalStandardProductName : "", className: 'reactgrid-cell' },
                        { type: "text", text: solvent.refBatchName ? solvent.refBatchName : "", className: 'reactgrid-cell' },
                        { type: "text", text: solvent.refWeight ? solvent.refWeight.toString() : "", className: "reactgrid-cell" },
                        { type: "text", text: "...", nonEditable: true, className: "reactgrid-button" }
                    ]
                }))
            ];
            */
        }
        else {
            let newSamples: NmrSolvent[] = [];
            newSamples.push({ solventId: new Guid().toString(), sampleName: "", editType: EditType.Insert });

            setSamples(newSamples);
        }

        setRows(createdRows);
    }

    //render

    /*
    function chemicalCellStyle(): CellStyle {
        let cellStyle: CellStyle = {
            background: "#efefef",
            color: "blue"
        };

        return cellStyle;
    }
    */

    function getEditTypeTemplate(nmrSolvent: NmrSolvent) {
        //console.log("getEditTypeTemplate");
        //console.log(nmrSolvent);

        switch (nmrSolvent.editType) {
            case EditType.None:
                return "";
            case EditType.Insert:
                return "reactgrid-add-icon";
            case EditType.Update:
                return "reactgrid-edit-icon";
            case EditType.Delete:
                return "reactgrid-delete-icon";
        }
    }

    //show/hide

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }
}

